// frontend/src/components/datahealth/DuplicateReview.jsx
//
// The duplicate-review body of the Data Health "duplicate_items" card. Lists the
// cross-origin item pairs the resolver judges to be the same product (a
// sales-channel row + its ERP/native twin) and lets the merchant merge a pair,
// or mark it "not the same". Merging is destructive and never automatic — the
// merchant confirms each one and chooses which row survives (defaulting to the
// suggested ERP-linked one).
//
// Rendered by DataHealth's TaskCard for actionType 'duplicate-review' instead of
// the generic AG-Grid IssueGrid — a pair with a keep/remove choice and a merge
// doesn't fit the flat-row + bulk-update model. The pair list and the merge /
// dismiss writes are owned by the identity API (useIdentity); this card only
// surfaces them. `onItemFixed` decrements the Data Health count as pairs leave.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, CircularProgress, Stack } from '@mui/material';

import StatusChip from '../common/StatusChip';
import { VENDOR_DISPLAY_NAMES } from '../../hooks/useSoR';
import { useDuplicates, useMergeDuplicate, useDismissDuplicate } from '../../hooks/useIdentity';
import { useInFlight } from '../../hooks/useInFlight';
import { translateRequestError } from '../../queries/api';
import '../../styles/pages.css';
import { showToast } from '../common/appToastBus';

// Which connector origin a row came from, for a "Source: …" chip.
function originLabel(row, t) {
  if (row.erp_id) return t('dataHealth.duplicates.originErp');
  if (row.shopify_variant_gid) return VENDOR_DISPLAY_NAMES.shopify;
  return t('dataHealth.duplicates.originManexys');
}

function ItemCell({ item, t }) {
  return (
    <div>
      {/* Name first, then the article code for this row's origin (SKU on a
          Shopify row, ERP code on an ERP one) — never ItemCode, which on a
          Shopify row is the variant id's numeric suffix. */}
      <div style={{ fontWeight: 'var(--font-weight-semibold)' }}>{item.name}</div>
      <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85em' }}>
        {item.article_code}
      </div>
      <StatusChip label={originLabel(item, t)} color="neutral" />
    </div>
  );
}

export default function DuplicateReview({ onItemFixed }) {
  const { t } = useTranslation();
  const { duplicates, count, loading, error } = useDuplicates();
  const merge = useMergeDuplicate();
  const dismiss = useDismissDuplicate();

  // Per-pair survivor override (keyed by the suggested-survivor id). Default is
  // the backend's suggestion (the ERP-linked / older row).
  const [keepLoser, setKeepLoser] = useState({});
  // Keyed by the suggested-survivor id, like keepLoser.
  const { running, track } = useInFlight();

  // A pair already resolved elsewhere answers `alreadyResolved` (resolvingRace
  // in useIdentity): the row leaves with a neutral toast, not an error.
  const resolve = (pair, action, request) => {
    return track(pair.suggested_survivor_id, action, request);
  };

  const handleMerge = (pair) => resolve(pair, 'merge', async () => {
    const swap = keepLoser[pair.suggested_survivor_id];
    const survivorId = swap ? pair.suggested_loser_id : pair.suggested_survivor_id;
    const loserId = swap ? pair.suggested_survivor_id : pair.suggested_loser_id;
    try {
      // useMergeDuplicate invalidates the duplicates query → the row leaves on
      // refetch; onItemFixed decrements the Data Health card count.
      const res = await merge.mutateAsync({ survivorId, loserId });
      onItemFixed?.();
      if (res?.alreadyResolved) showToast(t('dataHealth.duplicates.alreadyResolved'), 'info');
    } catch (err) {
      showToast(translateRequestError(err, t, 'dataHealth.duplicates.mergeFailed'), 'error');
    }
  });

  const handleDismiss = (pair) => resolve(pair, 'dismiss', async () => {
    try {
      const res = await dismiss.mutateAsync({
        idA: pair.suggested_survivor_id,
        idB: pair.suggested_loser_id,
      });
      onItemFixed?.();
      if (res?.alreadyResolved) showToast(t('dataHealth.duplicates.alreadyResolved'), 'info');
    } catch (err) {
      showToast(translateRequestError(err, t, 'dataHealth.duplicates.dismissFailed'), 'error');
    }
  });

  if (loading) {
    return (
      <div className="grid-loading-center">
        <CircularProgress size={24} />
      </div>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  // The card only renders when count > 0, but a merchant can clear the last pair
  // while it's open — show the resolved state rather than an empty table.
  if (count === 0) return <Alert severity="success">{t('dataHealth.duplicates.allDone')}</Alert>;

  return (
    <Stack spacing={1}>
      <table className="connectors-table">
        <thead>
          <tr>
            <th>{t('dataHealth.duplicates.colKeep')}</th>
            <th>{t('dataHealth.duplicates.colRemove')}</th>
            <th>{t('dataHealth.duplicates.colActions')}</th>
          </tr>
        </thead>
        <tbody>
          {duplicates.map((pair) => {
            const swap = keepLoser[pair.suggested_survivor_id];
            const keep = swap ? pair.loser : pair.survivor;
            const remove = swap ? pair.survivor : pair.loser;
            const action = running[pair.suggested_survivor_id];
            const busy = action !== undefined;
            return (
              <tr key={pair.suggested_survivor_id}>
                <td><ItemCell item={keep} t={t} /></td>
                <td><ItemCell item={remove} t={t} /></td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={busy}
                      onClick={() => setKeepLoser((prev) => ({
                        ...prev,
                        [pair.suggested_survivor_id]: !prev[pair.suggested_survivor_id],
                      }))}
                    >
                      {t('dataHealth.duplicates.swap')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      disabled={busy}
                      loading={action === 'merge'}
                      onClick={() => handleMerge(pair)}
                    >
                      {t('dataHealth.duplicates.merge')}
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      disabled={busy}
                      loading={action === 'dismiss'}
                      onClick={() => handleDismiss(pair)}
                    >
                      {t('dataHealth.duplicates.notSame')}
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

    </Stack>
  );
}
